import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { MatchService } from '../service/matchService';
import { PopupForm } from './popupForm';
import {
  BetBadge, LeagueCell, ListSkeleton, ProbabilityBar, ResultTag, StateMessage, TeamCell, VALUE_HINT,
} from './common';
import { capitalizeName, formatEdge, formatKickoffDay, formatKickoffTime, formatMoney, formatOdds, formatPct } from '../utils/format';
import { filterMatches, normalizeMatch, SORTS, sortMatches } from '../utils/matches';
import { loadSetting, saveSetting } from '../utils/storage';
import { parseLeagueKeys } from '../utils/leagues';

function Kickoff({ match }) {
  if (!match.kickoff) {
    return <span className="kickoff">{match.MatchTime}</span>;
  }
  return (
    <span className="kickoff">
      <span className="kickoff-time">{formatKickoffTime(match.kickoff)}</span>
      <span className="kickoff-day">{formatKickoffDay(match.kickoff)}</span>
    </span>
  );
}

function OddsTrio({ match }) {
  return (
    <span className="odds-trio">
      {match.outcomes.map((o) => (
        <span key={o.key} className={`odds-cell${match.pick && match.pick.key === o.key ? ' is-value' : ''}`}
          title={`${o.label}: ${formatOdds(o.odds)}`}>
          {formatOdds(o.odds)}
        </span>
      ))}
    </span>
  );
}

// Every outcome of one match with the model's numbers and a button to save it as a pick.
function OutcomeDetails({ match, onPick, compact = false }) {
  return (
    <div className="outcome-details">
      <div className="outcome-grid" role="table" aria-label={`Outcomes for ${capitalizeName(match.HomeTeamCode)} v ${capitalizeName(match.AwayTeamCode)}`}>
        <div className="outcome-row outcome-head" role="row">
          <span role="columnheader">Outcome</span>
          <span role="columnheader" title="The probability the bet is based on: market, rating engine and stats model">Win %</span>
          <span role="columnheader" title="Bookmaker odds">Odds</span>
          <span role="columnheader" title="Odds that would be fair if this probability is right">Fair</span>
          <span role="columnheader" title="Expected return per unit at these odds">Edge</span>
          <span role="columnheader"><span className="sr-only">Pick</span></span>
        </div>
        {match.outcomes.map((o) => (
          <div key={o.key} role="row" className={`outcome-row${match.pick === o ? ' is-value' : ''}${o.isResult ? ' is-result' : ''}`}>
            <span role="cell" className="outcome-name">
              {o.key === 'D' ? <span className="draw-name">Draw</span> : <TeamCell name={o.Result} image={o.Image} />}
              {match.pick === o && <span className="mini-tag">Model bet · {formatMoney(match.amount)}</span>}
            </span>
            <span role="cell">{formatPct(o.prob)}</span>
            <span role="cell">{formatOdds(o.odds)}</span>
            <span role="cell">{o.OddsPred ? Number(o.OddsPred).toFixed(2) : '–'}</span>
            <span role="cell" className={o.edge > 0 ? 'text-positive' : 'text-negative'}>{formatEdge(o.edge)}</span>
            <span role="cell">
              {!match.settled && (
                <Button label={compact ? undefined : 'Pick'} icon="pi pi-plus" size="small" outlined onClick={() => onPick(o)}
                  aria-label={`Add ${o.label} to my picks`} />
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchCard({ match, expanded, onToggle, onPick, showLeague }) {
  const id = `match-${match.PredId}`;
  return (
    <article className={`match-card${match.value ? ' is-value' : ''}`}>
      <div className="match-card-top">
        {showLeague && <LeagueCell league={match.League} image={match.CountryImagePath} />}
        <Kickoff match={match} />
      </div>
      <div className="match-card-teams">
        <TeamCell name={match.HomeTeamCode} image={match.HomeTeamImagePath} />
        <TeamCell name={match.AwayTeamCode} image={match.AwayTeamImagePath} />
      </div>
      <ProbabilityBar outcomes={match.outcomes} />
      <div className="match-card-bottom">
        {match.pick && <BetBadge match={match} />}
        <ResultTag match={match} />
        <Button label={expanded ? 'Hide' : 'Details'} icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
          iconPos="right" text size="small" onClick={onToggle} aria-expanded={expanded} aria-controls={id} />
      </div>
      {expanded && <div id={id}><OutcomeDetails match={match} onPick={onPick} compact /></div>}
    </article>
  );
}

const FILTER_PARAMS = ['q', 'league', 'sort', 'value'];

export function MatchTable({ isDesktop }) {
  const { area, league, date } = useParams();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [pickOutcome, setPickOutcome] = useState(null);
  const toast = useRef(null);
  const requestId = useRef(0);

  const query = searchParams.get('q') || '';
  // Toolbar league filter: several leagueKeys, comma-separated in ?league=.
  const leagueFilterParam = searchParams.get('league') || '';
  const leagueFilter = useMemo(() => parseLeagueKeys(leagueFilterParam), [leagueFilterParam]);
  // Leagues chosen in the top bar (/leagues?l=...): the page loads each of them.
  const selectedParam = pathname === '/leagues' ? (searchParams.get('l') || '') : '';
  const selectedKey = parseLeagueKeys(selectedParam).join(',');
  const sort = searchParams.get('sort') || loadSetting('sort', 'kickoff');
  const valueOnly = searchParams.has('value') ? searchParams.get('value') === '1' : loadSetting('valueOnly', false);
  const isLeaguePage = Boolean(area && league);
  const isMultiLeague = selectedKey !== '';

  const setFilter = useCallback((key, value) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === null || value === '' || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(() => {
    const id = ++requestId.current;
    setMatches(null);
    setError(null);
    (selectedKey ? MatchService.getMatchesForLeagues(selectedKey.split(',')) : MatchService.getMatchesOnLoad(area, league, date))
      .then((rows) => {
        if (id === requestId.current) {
          setMatches((Array.isArray(rows) ? rows : []).map((row) => normalizeMatch(row)));
        }
      })
      .catch((e) => {
        if (id === requestId.current) {
          setError(e);
        }
      });
  }, [area, league, date, selectedKey]);

  useEffect(() => {
    load();
    setExpanded({});
  }, [load]);

  const leagueOptions = useMemo(() => {
    const seen = new Map();
    (matches || []).forEach((m) => {
      if (!seen.has(m.leagueKey)) {
        seen.set(m.leagueKey, { label: `${capitalizeName(m.League)} · ${capitalizeName(m.Country)}`, value: m.leagueKey });
      }
    });
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [matches]);

  const visible = useMemo(() => (matches
    ? sortMatches(filterMatches(matches, { query, league: leagueFilter, valueOnly }), sort)
    : []), [matches, query, leagueFilter, valueOnly, sort]);
  const valueCount = (matches || []).filter((m) => m.value).length;
  const filtersActive = Boolean(query || leagueFilter.length || valueOnly);

  const clearFilters = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      FILTER_PARAMS.filter((k) => k !== 'sort').forEach((k) => next.delete(k));
      next.set('value', '0');
      return next;
    }, { replace: true });
    saveSetting('valueOnly', false);
  };

  const toggleRow = (match) => setExpanded((current) => {
    const next = { ...current };
    if (next[match.PredId]) {
      delete next[match.PredId];
    } else {
      next[match.PredId] = true;
    }
    return next;
  });

  const onPickSaved = (ok, outcome) => {
    setPickOutcome(null);
    toast.current.show(ok
      ? { severity: 'success', summary: 'Pick saved', detail: `${outcome.label} added to My picks`, life: 3000 }
      : { severity: 'error', summary: 'Could not save the pick', detail: 'Please try again.', life: 5000 });
  };

  const toolbar = (
    <div className="toolbar" role="search">
      <span className="p-input-icon-left toolbar-search">
        <i className="pi pi-search" />
        <InputText value={query} onChange={(e) => setFilter('q', e.target.value)} placeholder="Search teams or leagues"
          aria-label="Search teams or leagues" className="w-full" />
      </span>
      {!isLeaguePage && (
        <MultiSelect value={leagueFilter} options={leagueOptions} onChange={(e) => setFilter('league', (e.value || []).join(','))}
          placeholder="All leagues" showClear filter={leagueOptions.length > 8} className="toolbar-league"
          display="comma" maxSelectedLabels={2} selectedItemsLabel="{0} leagues" aria-label="Filter by league" />
      )}
      <Dropdown value={sort} options={SORTS} className="toolbar-sort" aria-label="Sort matches"
        onChange={(e) => { setFilter('sort', e.value); saveSetting('sort', e.value); }}
        valueTemplate={(option) => <span><i className="pi pi-sort-alt mr-2" aria-hidden="true" />{option ? option.label : 'Sort'}</span>} />
      <label className="toolbar-toggle" title={VALUE_HINT}>
        <InputSwitch checked={valueOnly} inputId="value-only"
          onChange={(e) => { setFilter('value', e.value ? '1' : '0'); saveSetting('valueOnly', e.value); }} />
        <span>Bets only</span>
      </label>
    </div>
  );

  let body;
  if (error) {
    body = <StateMessage icon="pi-exclamation-triangle" title="Matches could not be loaded"
      detail="The prediction server did not answer. Check your connection and try again." actionLabel="Retry" onAction={load} />;
  } else if (matches === null) {
    body = <ListSkeleton />;
  } else if (matches.length === 0) {
    body = <StateMessage icon="pi-calendar-times" title="No predictions for this day"
      detail={isLeaguePage ? 'This league has no upcoming fixtures with predictions yet.'
        : (isMultiLeague ? 'None of these leagues has upcoming fixtures with predictions yet.' : 'Try another day with the arrows above.')} />;
  } else if (visible.length === 0) {
    body = <StateMessage icon="pi-filter-slash" title="No matches fit these filters"
      detail={valueOnly ? 'The model has no bet in these matches.' : undefined} actionLabel="Clear filters" onAction={clearFilters} />;
  } else if (isDesktop) {
    body = (
      <DataTable value={visible} dataKey="PredId" className="match-table"
        expandedRows={expanded} onRowToggle={(e) => setExpanded(e.data)}
        rowExpansionTemplate={(m) => <OutcomeDetails match={m} onPick={setPickOutcome} />}
        rowClassName={(m) => ({ 'is-value': m.value, 'is-settled': m.settled })}>
        <Column expander style={{ width: '3rem' }} />
        <Column field="MatchTime" header="Kick-off" body={(m) => <Kickoff match={m} />} style={{ width: '7rem' }} />
        {!isLeaguePage && <Column field="League" header="League" body={(m) => <LeagueCell league={m.League} image={m.CountryImagePath} />} />}
        <Column field="HomeTeamCode" header="Match" body={(m) => (
          <span className="teams-stack">
            <TeamCell name={m.HomeTeamCode} image={m.HomeTeamImagePath} />
            <TeamCell name={m.AwayTeamCode} image={m.AwayTeamImagePath} />
          </span>
        )} />
        <Column field="WinProb" header={<span title="The probabilities the bet is based on: market, rating engine and stats model">Win % 1 · X · 2</span>}
          body={(m) => <ProbabilityBar outcomes={m.outcomes} />} style={{ minWidth: '13rem' }} />
        <Column field="WinOdds" header={<span title="Bookmaker odds: home, draw, away">Odds</span>} body={(m) => <OddsTrio match={m} />} />
        <Column field="PredId" header={<span title={VALUE_HINT}>Bet · stake</span>} body={(m) => (
          <span className="edge-stack"><BetBadge match={m} /><ResultTag match={m} /></span>
        )} />
      </DataTable>
    );
  } else {
    body = (
      <div className="match-cards">
        {visible.map((m) => (
          <MatchCard key={m.PredId} match={m} expanded={Boolean(expanded[m.PredId])} onToggle={() => toggleRow(m)}
            onPick={setPickOutcome} showLeague={!isLeaguePage} />
        ))}
      </div>
    );
  }

  return (
    <section className="panel" aria-live="polite">
      <Toast ref={toast} position={isDesktop ? 'top-right' : 'bottom-center'} />
      {toolbar}
      {matches && matches.length > 0 && (
        <p className="result-count">
          {visible.length === matches.length ? `${matches.length} matches` : `${visible.length} of ${matches.length} matches`}
          {valueCount > 0 && <> · <span className="text-value">{valueCount} {valueCount === 1 ? 'bet' : 'bets'}</span></>}
          {filtersActive && <Button label="Clear filters" link size="small" onClick={clearFilters} className="ml-1 p-0" />}
        </p>
      )}
      {body}
      <Dialog header="Add to my picks" visible={Boolean(pickOutcome)} onHide={() => setPickOutcome(null)}
        className="pick-dialog" breakpoints={{ '640px': '95vw' }} style={{ width: '28rem' }} dismissableMask>
        {pickOutcome && <PopupForm match={pickOutcome} onDone={onPickSaved} />}
      </Dialog>
    </section>
  );
}
